import type { CSSProperties } from "react";

interface SpinnerProps {
  size?: "sm" | "lg";
  className?: string;
  style?: CSSProperties;
}

export function Spinner({ size = "sm", className = "", style }: SpinnerProps) {
  const cls = size === "lg" ? "spinner spinner-lg" : "spinner";
  return (
    <span
      className={`${cls} ${className}`.trim()}
      style={style}
      role="status"
      aria-label="Loading"
    />
  );
}

interface SkeletonProps {
  variant?: "text" | "title" | "chip" | "block" | "card";
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: CSSProperties;
}

export function Skeleton({
  variant = "text",
  width,
  height,
  className = "",
  style,
}: SkeletonProps) {
  const baseStyle: CSSProperties = {
    width: width ?? undefined,
    height: height ?? undefined,
    ...style,
  };
  return (
    <span
      className={`skeleton ${variant} ${className}`.trim()}
      style={baseStyle}
      aria-hidden="true"
    />
  );
}

export function SkeletonStatGrid({ count = 3 }: { count?: number }) {
  return (
    <div className="account-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div className="stat-card" key={i}>
          <Skeleton variant="text" width={90} style={{ marginBottom: 12 }} />
          <Skeleton variant="title" width="65%" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonText({
  lines = 3,
  lastWidth = "60%",
}: {
  lines?: number;
  lastWidth?: string;
}) {
  return (
    <div className="skeleton-row" style={{ gap: 10 }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={i === lines - 1 ? lastWidth : "100%"}
        />
      ))}
    </div>
  );
}

export function SkeletonTableRows({
  rows = 5,
  cols = 6,
}: {
  rows?: number;
  cols?: number;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }).map((_, c) => (
            <td key={c}>
              <Skeleton variant="text" width={c === 0 ? "85%" : "60%"} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function FullscreenLoader({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="page-loading">
      <Spinner size="lg" />
      <span>{label}</span>
    </div>
  );
}
